import _ from 'lodash';
import { getNestedKeys, getNestedObject, isObject, isArray } from 'utils/helpers';
import { FIELD_TYPES } from './constants';

// https://www.elastic.co/guide/en/elasticsearch/reference/5.6/indices-put-mapping.html
export const createFieldMappings = (fields /*: Object */) => {
  const mappingsUpdatesObj = getNestedObject(fields);
  const getMapping = (field) => _.keys(field).map((k) => ({ fieldName: k, fieldType: field[k] }));

  const mappings = _.keys(mappingsUpdatesObj).map((k) => {
    if (isObject(mappingsUpdatesObj[k])) {
      return { fieldName: k, fieldType: 'object', children: getMapping(mappingsUpdatesObj[k]) };
    }
    return { fieldName: k, fieldType: mappingsUpdatesObj[k] };
  });
  return mappings;
};

// type NewFieldsCsv = {
//   newFields: Array<string>,
//   rowData: Array<Object>,
// };

// TODO if (when?) csv parsing in backend allows for `objects` and lists of objects in csv file, you will need to update this method
// see `getSampleDataFromJson` below
export const getSampleDataFromCsv = ({ newFields, rowData } /*: NewFieldsCsv */) =>
  newFields.map((f, i) => {
    const sample1 = _.get(
      _.find(rowData, (r) => r[f]),
      f,
    );
    const sample2 = _.get(
      _.find(rowData, (r) => r[f] && r[f] !== sample1),
      f,
    );

    return {
      fieldName: f,
      sample1,
      sample2,
      id: i,
    };
  });

// type NewFieldsJson = {
//   newFields: Array<string>,
//   tableValue: Object | string,
// };

export const getSampleDataFromJson = ({ newFields, tableValue } /*: NewFieldsJson */) =>
  newFields.map((f, i) => {
    let sample1;
    try {
      const jsonVal = JSON.parse(tableValue);
      const keys = f.split('.');
      // parsed sample could be from a collection of objects
      const parsed = _.get(jsonVal, keys) || _.get(jsonVal, [keys[0], '0', ...keys.slice(1)]);
      sample1 =
        typeof parsed === 'object' || typeof parsed === 'boolean' ? JSON.stringify(parsed) : parsed;
    } catch (err) {
      sample1 = '';
    }

    return {
      fieldName: f,
      sample1,
      id: i,
    };
  });

// type Sample = string | null;

export const checkIfParentGeo = ({ keysInDotNotation, fields }) => {
  const keys = keysInDotNotation.split('.');
  const parentKeys = keys.length > 1 ? keys.slice(0, keys.length - 1) : keys;
  const parentKey = parentKeys.join('.');
  return keys.length > 1 && _.get(fields, parentKey) === FIELD_TYPES.geo_location;
};

export const checkIfGeo = (sample /*: Sample */) => {
  try {
    const obj = JSON.parse(sample);
    const keys = Object.keys(obj);
    return keys.length === 2 && _.intersection(keys, ['lat', 'lon']).length === 2;
  } catch (err) {
    return false;
  }
};

export const checkIfObject = (sample /*: Sample */) =>
  sample && sample[0] === '{' && _.last(sample) === '}';

export const checkIfKeysNested = (key /*: string */) => key && key.split('.').length > 1;

export const checkIfBoolean = (sample /*: Sample */) => sample === 'true' || sample === 'false';

export const checkIfLong = (sample /*: Sample */) => {
  const regex = /\./g;
  return sample && !regex.test(sample) && _.isInteger(Number(sample));
};

export const checkIfDouble = (sample /*: Sample */) => {
  const regex = /\./g;
  return regex.test(sample) && !_.isNaN(Number(sample));
};

export const checkIfString = (sample /*: Sample */) =>
  _.isNaN(Number(sample)) && !checkIfObject(sample) && !checkIfBoolean(sample);

export const getParsedJson = (sample /*: Sample */) => {
  let parsed;
  try {
    parsed = JSON.parse(sample);
  } catch (err) {
    parsed = null;
  }
  return parsed;
};

export const checkIfArray = (sample /*: Sample */) => isArray(getParsedJson(sample));

export const checkIfEmpty = (sample /*: Sample */) => _.isEmpty(getParsedJson(sample));

export const getFirstItemInArray = (sample /*: Sample */) =>
  JSON.stringify(_.first(getParsedJson(sample)));

// TODO checkIfDate

// type Samples = {
//   sample1: Sample,
//   sample2: ?Sample,
//   fieldName: ?string,
// };

export const getTypeSuggestion = ({ sample1, sample2, fieldName } /*: Samples */) => {
  if (sample1 || sample2) {
    if (checkIfArray(sample1) || checkIfArray(sample2)) {
      // get the first item in array and get its type suggestion
      return [
        getTypeSuggestion({
          sample1: getFirstItemInArray(sample1),
          sample2: getFirstItemInArray(sample2),
        }),
      ];
    }
    if (checkIfGeo(sample1) || checkIfGeo(sample2)) {
      return FIELD_TYPES.geo_location;
    }
    if (
      (checkIfObject(sample1) && !checkIfKeysNested(fieldName)) ||
      (checkIfObject(sample2) && !checkIfKeysNested(fieldName))
    ) {
      return FIELD_TYPES.object;
    }
    if (checkIfBoolean(sample1) || checkIfBoolean(sample2)) {
      return FIELD_TYPES.boolean;
    }
    if (checkIfDouble(sample1) || checkIfDouble(sample2)) {
      return FIELD_TYPES.double;
    }
    if (checkIfLong(sample1) || checkIfLong(sample2)) {
      return FIELD_TYPES.long;
    }
    if (checkIfString(sample1) || checkIfString(sample2)) {
      return FIELD_TYPES.string;
    }
  }
  return null;
};

// type RowsFields = {
//   rows: Array<Object>,
//   fields: Object,
// };

// suggestedTypes are different for arrays and should just represent the initial default suggested value of field types based on sample data
// updatedFields is what will actually change the fields data model (and gets sent in the payload when you update field types)
export const getFieldsWithSuggestions = ({ rows, fields } /*: RowsFields */) => {
  const suggestedTypes = {};
  const updatedFields = { ...fields };
  rows.forEach((row) => {
    const { fieldName, fieldType, sample1, sample2 } = row;
    // if sample1 or sample2 exist and the fieldType isn't yet set in fields
    // set the fieldType to the suggestedType
    if (!fields[fieldName] && (fieldType === 'undefined' || !fieldType)) {
      const suggestedType = getTypeSuggestion({ sample1, sample2, fieldName });
      const suggestedTypeIsArray = isArray(suggestedType);
      // array types will need helper text
      if (suggestedTypeIsArray) {
        suggestedTypes[fieldName] = suggestedType;
        updatedFields[fieldName] = _.first(suggestedType);
      } else if (suggestedType) {
        suggestedTypes[fieldName] = suggestedType;
        updatedFields[fieldName] = suggestedType;
      }
    }
  });
  return { suggestedTypes, updatedFields };
};

// check to see if value is a collection of objects
export const isCollection = (val) => _.isArray(val) && _.isObject(_.first(val));

// because ES doesn't have a concept of arrays a collection of objects is the same as a single object
// [{ key: 'val' }] is the same as { key: 'val' }
export const getCollectionKeys = (parentKey, collectionObj) => {
  const nestedKeys = getNestedKeys(collectionObj, true);
  return nestedKeys.map((k) => `${parentKey}.${k}`);
};

// extend keys to include any collections
// filter out any geo_location children
export const getKeysFromSample = ({ sample, definedFields }) =>
  getNestedKeys(sample, true)
    .reduce(
      (acc, k) =>
        isCollection(sample[k])
          ? [...acc, k, ...getCollectionKeys(k, _.first(sample[k]))]
          : [...acc, k],
      [],
    )
    .filter((dotKey) => !checkIfParentGeo({ keysInDotNotation: dotKey, fields: definedFields }));
